<template>
  <div class="qr-login-panel">
    <div v-if="isWechat && loading" class="qr-login-panel__state">
      <div class="qr-login-panel__spinner" />
      <p>微信授权中...</p>
    </div>

    <div v-else-if="isWechat && error" class="qr-login-panel__state is-error">
      <p>{{ errorMsg }}</p>
      <button type="button" class="qr-login-panel__btn" @click="retryWechatAuth">
        重新授权
      </button>
    </div>

    <template v-else-if="!isWechat">
      <h3 class="qr-login-panel__title">微信扫码登录</h3>

      <div v-if="qrcodeStatus === QR_LOGIN_STATUS.LOADING" class="qr-login-panel__state">
        <div class="qr-login-panel__spinner" />
        <p>生成二维码...</p>
      </div>

      <div v-else-if="qrcodeStatus === QR_LOGIN_STATUS.PENDING" class="qr-login-panel__box">
        <img
          v-if="qrcodeDataUrl"
          :src="qrcodeDataUrl"
          alt="扫码登录"
          class="qr-login-panel__image"
        />
        <p class="qr-login-panel__hint">请使用微信扫一扫</p>
        <p class="qr-login-panel__expire">二维码 {{ expireCountdown }} 秒后过期</p>
      </div>

      <div v-else-if="qrcodeStatus === QR_LOGIN_STATUS.SCANNED" class="qr-login-panel__box">
        <p class="qr-login-panel__success">已扫码，请在手机上确认</p>
      </div>

      <div v-else-if="qrcodeStatus === QR_LOGIN_STATUS.EXPIRED" class="qr-login-panel__box">
        <p class="qr-login-panel__muted">二维码已过期</p>
        <button type="button" class="qr-login-panel__btn" @click="generateQrcode">
          刷新二维码
        </button>
      </div>

      <div v-else-if="qrcodeStatus === QR_LOGIN_STATUS.ERROR" class="qr-login-panel__box">
        <p class="qr-login-panel__error">{{ errorMsg }}</p>
        <button type="button" class="qr-login-panel__btn" @click="generateQrcode">
          重试
        </button>
      </div>

    </template>
  </div>
</template>

<script>
import {
  checkLoginQrcodeStatus,
  generateLoginQrcode,
  loginByOpenid
} from '@/api/auth'
import {
  QR_DEFAULT_EXPIRE_SECONDS,
  QR_IMAGE_SERVICE_BASE,
  QR_IMAGE_SIZE,
  QR_LOGIN_STATUS,
  QR_POLL_INTERVAL_MS,
  WECHAT_OAUTH_BASE
} from '@/constants/auth'

export default {
  name: 'QrLoginPanel',
  props: {
    redirect: {
      type: String,
      default: '/'
    }
  },
  data() {
    return {
      QR_LOGIN_STATUS,
      isWechat: false,
      loading: true,
      error: false,
      errorMsg: '',
      qrcodeStatus: QR_LOGIN_STATUS.LOADING,
      qrcodeUuid: '',
      qrcodeUrl: '',
      qrcodeDataUrl: '',
      expireCountdown: QR_DEFAULT_EXPIRE_SECONDS,
      pollTimer: null,
      countdownTimer: null
    }
  },
  computed: {
    authCallbackUrl() {
      const base = `${window.location.origin}${window.location.pathname}`
      return `${base}#/auth?redirect=${encodeURIComponent(this.redirect)}`
    },
    wechatDirectUrl() {
      return `${WECHAT_OAUTH_BASE}?redirect_url=${encodeURIComponent(this.authCallbackUrl)}`
    }
  },
  created() {
    this.initAuth()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    initAuth() {
      this.isWechat = /micromessenger/i.test(navigator.userAgent)
      if (this.isWechat) {
        this.handleWechatAuth()
      } else {
        this.generateQrcode()
      }
    },
    handleWechatAuth() {
      const openid = new URLSearchParams(window.location.search).get('openid')
      if (openid && openid !== 'undefined') {
        this.loginByOpenid(openid)
      } else {
        this.gotoWechatAuth()
      }
    },
    loginByOpenid(openid) {
      loginByOpenid(openid)
        .then(result => {
          if (result.status === 1 && result.data) {
            this.$emit('success', {
              token: result.data.token,
              user: result.data.user
            })
            return
          }
          this.error = true
          this.errorMsg = result.desc || result.msg || '登录失败'
          this.loading = false
        })
        .catch(() => {
          this.error = true
          this.errorMsg = '网络错误，请重试'
          this.loading = false
        })
    },
    gotoWechatAuth() {
      window.location.href = this.wechatDirectUrl
    },
    retryWechatAuth() {
      this.error = false
      this.loading = true
      this.gotoWechatAuth()
    },
    generateQrcode() {
      this.clearTimers()
      this.qrcodeStatus = QR_LOGIN_STATUS.LOADING
      this.expireCountdown = QR_DEFAULT_EXPIRE_SECONDS

      generateLoginQrcode()
        .then(result => {
          if (result.status === 1 && result.data) {
            this.qrcodeUuid = result.data.uuid
            this.qrcodeUrl = result.data.qrcode_url
            this.expireCountdown = result.data.expire_seconds || QR_DEFAULT_EXPIRE_SECONDS
            this.qrcodeDataUrl = `${QR_IMAGE_SERVICE_BASE}?size=${QR_IMAGE_SIZE}x${QR_IMAGE_SIZE}&data=${encodeURIComponent(this.qrcodeUrl)}`
            this.qrcodeStatus = QR_LOGIN_STATUS.PENDING
            this.startPolling()
            this.startCountdown()
            return
          }
          this.qrcodeStatus = QR_LOGIN_STATUS.ERROR
          this.errorMsg = result.desc || result.msg || '生成二维码失败'
        })
        .catch(() => {
          this.qrcodeStatus = QR_LOGIN_STATUS.ERROR
          this.errorMsg = '网络错误，请重试'
        })
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        this.checkQrcodeStatus()
      }, QR_POLL_INTERVAL_MS)
    },
    checkQrcodeStatus() {
      if (!this.qrcodeUuid) return

      checkLoginQrcodeStatus(this.qrcodeUuid)
        .then(result => {
          if (result.status !== 1 || !result.data) return

          const status = result.data.status
          if (status === 1) {
            this.qrcodeStatus = QR_LOGIN_STATUS.SCANNED
          } else if (status === 2) {
            this.clearTimers()
            this.qrcodeStatus = QR_LOGIN_STATUS.SUCCESS
            if (result.data.token) {
              this.$emit('success', {
                token: result.data.token,
                user: result.data.user_info
              })
            }
          } else if (status === 3) {
            this.clearTimers()
            this.qrcodeStatus = QR_LOGIN_STATUS.EXPIRED
          }
        })
        .catch(() => {})
    },
    startCountdown() {
      this.countdownTimer = setInterval(() => {
        this.expireCountdown -= 1
        if (this.expireCountdown <= 0) {
          this.clearTimers()
          this.qrcodeStatus = QR_LOGIN_STATUS.EXPIRED
        }
      }, 1000)
    },
    clearTimers() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    }
  }
}
</script>

<style scoped>
.qr-login-panel {
  text-align: center;
}

.qr-login-panel__title {
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
}

.qr-login-panel__box,
.qr-login-panel__state {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-md);
}

.qr-login-panel__image {
  width: var(--qr-login-image-size);
  height: var(--qr-login-image-size);
  display: block;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-bg-page);
}

.qr-login-panel__hint {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
}

.qr-login-panel__expire,
.qr-login-panel__muted {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.qr-login-panel__success {
  font-size: var(--font-size-md);
  color: #07c160;
}

.qr-login-panel__error,
.qr-login-panel__state.is-error {
  color: #dc2626;
}

.qr-login-panel__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: qr-login-spin 0.8s linear infinite;
}

.qr-login-panel__btn {
  padding: var(--space-sm) var(--space-xl);
  border: none;
  border-radius: var(--radius-pill);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: var(--font-size-md);
}

@keyframes qr-login-spin {
  to { transform: rotate(360deg); }
}
</style>
